<template>
  <div class="home" :style="{ backgroundImage: `url(${backgroundImage})` }">
    <!-- 顶部横幅 -->
    <div class="home-banner">
      <div class="banner-content">
        <h1 class="banner-title">国家中医药监督执法实训基地（湖北）学习平台</h1>
        <p class="banner-subtitle">National Traditional Chinese Medicine Supervision and Enforcement Training Base (Hubei) Learning PLatform</p>
        <div v-if="workGuidance.enabled" class="banner-actions">
          <el-button class="banner-guidance-btn" round @click="openWorkGuidance">
            {{ workGuidance.buttonText || '工作指引' }}
          </el-button>
        </div>
      </div>
    </div>

    <div v-if="passwordWarning && isAuthenticated" class="password-warning-bar">
      <el-alert type="warning" :title="passwordWarning.message" show-icon :closable="false">
        <el-button type="primary" size="small" @click="$router.push('/change-password')">立即修改密码</el-button>
      </el-alert>
    </div>

    <!-- 主内容：左侧 2x2 板块 + 右侧学习排行榜 -->
    <div class="main-content home-layout">
      <div class="left-column">
        <div class="blocks-grid">
          <!-- 政策法规 -->
      <div v-if="homepageConfig.showPolicies" class="content-block fixed-block">
        <div class="block-header">
          <el-icon class="block-icon"><Document /></el-icon>
          <h2 class="block-title">政策法规</h2>
          <div class="block-line"></div>
        </div>
        <div class="block-search">
          <el-input
            v-model="policySearch"
            placeholder="搜索政策法规..."
            clearable
            size="small"
            class="block-search-input"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>
        <div v-loading="loadingPolicies" class="block-list-wrap">
          <div v-if="filteredPolicies.length === 0" class="empty-state">
            <el-empty description="暂无政策法规" :image-size="60" />
          </div>
          <ul v-else class="policies-list scroll-list">
            <li
              v-for="policy in filteredPolicies"
              :key="policy.id"
              class="policy-item"
              @click="viewPolicy(policy)"
            >
              <div class="policy-bullet"></div>
              <div class="policy-content">
                <h4 class="policy-title">{{ policy.title }}</h4>
                <p v-if="showPolicyExcerpt" class="policy-excerpt">{{ policy.content }}</p>
              </div>
              <el-icon class="policy-arrow"><ArrowRight /></el-icon>
            </li>
          </ul>
        </div>
      </div>

      <!-- 推荐课件 -->
      <div v-if="homepageConfig.showRecommendedCourseware" class="content-block fixed-block">
        <div class="block-header">
          <el-icon class="block-icon"><Document /></el-icon>
          <h2 class="block-title">推荐课件</h2>
          <div class="block-line"></div>
        </div>
        <div class="block-search">
          <el-input
            v-model="coursewareSearch"
            placeholder="搜索课件..."
            clearable
            size="small"
            class="block-search-input"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>
        <div v-loading="loadingCourseware" class="block-list-wrap">
          <div v-if="filteredCourseware.length === 0" class="empty-state">
            <el-empty description="暂无推荐课件" :image-size="60" />
          </div>
          <ul v-else class="courses-list scroll-list">
            <li
              v-for="item in filteredCourseware"
              :key="item.id"
              class="course-row"
              @click="viewFeaturedItem(item)"
            >
              <span class="course-row-title">{{ item.title }}</span>
              <el-icon class="policy-arrow"><ArrowRight /></el-icon>
            </li>
          </ul>
        </div>
      </div>

      <!-- 推荐视频 -->
      <div v-if="homepageConfig.showRecommendedVideos" class="content-block fixed-block">
        <div class="block-header">
          <el-icon class="block-icon"><View /></el-icon>
          <h2 class="block-title">推荐视频</h2>
          <div class="block-line"></div>
        </div>
        <div class="block-search">
          <el-input
            v-model="videoSearch"
            placeholder="搜索视频..."
            clearable
            size="small"
            class="block-search-input"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>
        <div v-loading="loadingVideos" class="block-list-wrap">
          <div v-if="filteredVideos.length === 0" class="empty-state">
            <el-empty description="暂无推荐视频" :image-size="60" />
          </div>
          <ul v-else class="courses-list scroll-list">
            <li
              v-for="item in filteredVideos"
              :key="item.id"
              class="course-row"
              @click="viewFeaturedItem(item)"
            >
              <span class="course-row-title">{{ item.title }}</span>
              <el-icon class="policy-arrow"><ArrowRight /></el-icon>
            </li>
          </ul>
        </div>
      </div>

      <!-- 证书 -->
      <div class="content-block fixed-block">
        <div class="block-header">
          <el-icon class="block-icon"><Trophy /></el-icon>
          <h2 class="block-title">证书</h2>
          <div class="block-line"></div>
        </div>
        <div class="block-search">
          <el-input
            v-model="certSearch"
            placeholder="搜索证书..."
            clearable
            size="small"
            class="block-search-input"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>
        <div v-loading="loadingCertificates" class="block-list-wrap">
          <div v-if="filteredCertificates.length === 0" class="empty-state">
            <el-empty :description="isAuthenticated ? '暂无证书' : '登录后查看我的证书'" :image-size="60" />
          </div>
          <ul v-else class="certs-list scroll-list">
            <li
              v-for="cert in filteredCertificates"
              :key="cert.id"
              class="cert-row"
              @click="goToCertificate(cert.id)"
            >
              <span class="cert-row-title">{{ cert.course_title || '证书' }}</span>
              <el-icon class="policy-arrow"><ArrowRight /></el-icon>
            </li>
          </ul>
        </div>
      </div>
        </div>
      </div>

      <!-- 分割线（不醒目） -->
      <div v-if="homepageConfig.showRanking" class="layout-divider" aria-hidden="true"></div>

      <!-- 右侧：学习排行榜 -->
      <aside v-if="homepageConfig.showRanking" class="ranking-column">
        <div class="content-block ranking-block">
          <div class="block-header">
            <el-icon class="block-icon"><Trophy /></el-icon>
            <h2 class="block-title">学习排行榜</h2>
            <div class="block-line"></div>
          </div>
          <div class="ranking-tabs-wrapper">
            <el-radio-group v-model="rankingType" size="default" @change="loadRanking">
              <el-radio-button label="courses">课程数</el-radio-button>
              <el-radio-button label="certificates">证书数</el-radio-button>
            </el-radio-group>
          </div>
          <div v-loading="loadingRanking" class="block-body">
            <div v-if="ranking.length === 0" class="empty-state">
              <el-empty description="暂无排行榜数据" />
            </div>
            <div v-else class="ranking-list">
              <div
                v-for="item in ranking"
                :key="item.student_id"
                class="ranking-item"
                :class="{ 'top-three': item.rank <= 3 }"
              >
                <div class="rank-number" :class="getRankClass(item.rank)">
                  <span v-if="item.rank <= 3" class="medal">
                    <el-icon v-if="item.rank === 1"><Trophy /></el-icon>
                    <el-icon v-else-if="item.rank === 2"><Star /></el-icon>
                    <el-icon v-else-if="item.rank === 3"><Star /></el-icon>
                  </span>
                  <span v-else>{{ item.rank }}</span>
                </div>
                <div class="rank-info">
                  <div class="rank-name">{{ item.username }}</div>
                  <div class="rank-stats">
                    <span v-if="rankingType === 'courses'">
                      完成: {{ item.completed_courses || 0 }} / 报名: {{ item.enrolled_courses || 0 }}
                    </span>
                    <span v-else>证书: {{ item.certificates_count || 0 }} 个</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <footer class="home-beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">鄂ICP备2025100712号-2</a>
    </footer>

    <!-- 配置按钮（仅管理员可见） -->
    <el-button
      v-if="isAdmin"
      class="config-button"
      type="primary"
      :icon="Setting"
      circle
      @click="showConfigDialog = true"
    />

    <!-- 首页配置对话框 -->
    <el-dialog
      v-model="showConfigDialog"
      title="首页内容配置"
      width="500px"
    >
      <el-form :model="homepageConfig" label-width="150px">
        <el-form-item label="显示法律法规">
          <el-switch v-model="homepageConfig.showPolicies" />
        </el-form-item>
        <el-form-item label="显示推荐课件">
          <el-switch v-model="homepageConfig.showRecommendedCourseware" />
        </el-form-item>
        <el-form-item label="显示推荐视频">
          <el-switch v-model="homepageConfig.showRecommendedVideos" />
        </el-form-item>
        <el-form-item label="显示排行榜">
          <el-switch v-model="homepageConfig.showRanking" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showConfigDialog = false">取消</el-button>
        <el-button type="primary" @click="saveConfig">保存</el-button>
      </template>
    </el-dialog>

    <!-- 政策法规阅读框：先打开阅读，再自行选择是否下载 -->
    <el-dialog
      v-model="policyDialogVisible"
      :title="selectedPolicy?.title"
      width="60%"
      class="policy-detail-dialog"
    >
      <div v-loading="policyDetailLoading" class="policy-detail" v-if="selectedPolicy">
        <div class="policy-meta">
          <span>发布时间: {{ formatDate(selectedPolicy.created_at) }}</span>
          <span v-if="selectedPolicy.updated_at">更新时间: {{ formatDate(selectedPolicy.updated_at) }}</span>
        </div>
        <!-- 有正文时直接在此阅读 -->
        <div class="policy-body" v-if="selectedPolicy.content" v-html="selectedPolicy.content"></div>
        <!-- 无正文但有文件时：弹出文件浏览窗口，方便查看后判断是否下载 -->
        <div v-else-if="selectedPolicy.file_path || selectedPolicy.file_url" class="policy-file-preview">
          <FilePreviewer
            :file-data="policyFileData"
            @download="downloadPolicyFile(selectedPolicy)"
          />
        </div>
        <div v-else class="policy-no-content">
          <el-empty description="暂无详细内容" />
        </div>
      </div>
      <template #footer>
        <div class="policy-dialog-footer">
          <span class="policy-dialog-tip">阅读后可选择下载</span>
          <el-button
            v-if="selectedPolicy && (selectedPolicy.file_path || selectedPolicy.file_url)"
            type="primary"
            @click="downloadPolicyFile(selectedPolicy)"
          >
            <el-icon><Download /></el-icon>
            下载文件
          </el-button>
          <el-button
            v-else-if="selectedPolicy && selectedPolicy.content"
            type="primary"
            @click="downloadPolicyAsText(selectedPolicy)"
          >
            <el-icon><Download /></el-icon>
            下载为文本
          </el-button>
          <el-button @click="policyDialogVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>

    <el-dialog v-model="featuredDialogVisible" :title="featuredItem?.title" width="70%" class="policy-detail-dialog">
      <FilePreviewer v-if="featuredItem" :file-data="featuredFileData" />
      <template #footer>
        <el-button @click="featuredDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <WorkGuidanceEntry ref="workGuidanceEntry" @loaded="onWorkGuidanceLoaded" />
    <SiteAnnouncementDialog scene="home" />
  </div>
</template>

<script>
import { User, Star, View, ArrowRight, Trophy, Document, Setting, Link, Download, Search } from '@element-plus/icons-vue'
import { isAuthenticated, getUserInfo, setUserInfo } from '@/services/auth'
import api from '@/services/api'
import { getPolicies, getStudentRanking, getDocumentById, getMyCertificates, getRecommendedCourseware, getRecommendedVideos } from '@/services/api'
import FilePreviewer from '@/components/FilePreviewer.vue'
import WorkGuidanceEntry from '@/components/WorkGuidanceEntry.vue'
import SiteAnnouncementDialog from '@/components/SiteAnnouncementDialog.vue'
import backgroundImage from '@/assets/background.jpg'

// 默认配置
const DEFAULT_CONFIG = {
  showRecommendedCourseware: true,
  showRecommendedVideos: true,
  showPolicies: true,
  showRanking: true
}

export default {
  name: 'HomeView',
  components: {
    User,
    Star,
    View,
    ArrowRight,
    Trophy,
    Document,
    Setting,
    Link,
    Download,
    Search,
    FilePreviewer,
    WorkGuidanceEntry,
    SiteAnnouncementDialog
  },
  data() {
    return {
      isAuthenticated: false,
      isAdmin: false,
      recommendedCourseware: [],
      recommendedVideos: [],
      policies: [],
      certificates: [],
      ranking: [],
      loadingCourseware: false,
      loadingVideos: false,
      loadingPolicies: false,
      loadingCertificates: false,
      loadingRanking: false,
      rankingType: 'courses',
      policySearch: '',
      coursewareSearch: '',
      videoSearch: '',
      certSearch: '',
      policyDialogVisible: false,
      policyDetailLoading: false,
      selectedPolicy: null,
      featuredDialogVisible: false,
      featuredItem: null,
      showConfigDialog: false,
      homepageConfig: { ...DEFAULT_CONFIG },
      backgroundImage: backgroundImage,
      showPolicyExcerpt: false,
      passwordWarning: null,
      workGuidance: { enabled: false }
    }
  },
  computed: {
    filteredPolicies() {
      const q = (this.policySearch || '').trim().toLowerCase()
      if (!q) return this.policies
      return this.policies.filter(
        p => (p.title && p.title.toLowerCase().includes(q)) ||
          (p.content && p.content.toLowerCase().includes(q))
      )
    },
    filteredCourseware() {
      const q = (this.coursewareSearch || '').trim().toLowerCase()
      if (!q) return this.recommendedCourseware
      return this.recommendedCourseware.filter(item => (item.title || '').toLowerCase().includes(q))
    },
    filteredVideos() {
      const q = (this.videoSearch || '').trim().toLowerCase()
      if (!q) return this.recommendedVideos
      return this.recommendedVideos.filter(item => (item.title || '').toLowerCase().includes(q))
    },
    filteredCertificates() {
      const q = (this.certSearch || '').trim().toLowerCase()
      if (!q) return this.certificates
      return this.certificates.filter(
        c => (c.course_title && c.course_title.toLowerCase().includes(q))
      )
    },
    policyFileData() {
      const p = this.selectedPolicy
      if (!p || (!p.file_path && !p.file_url)) return {}
      return {
        title: p.title,
        file_path: p.file_path,
        file_url: p.file_url,
        file_type: p.file_type || 'document',
        file_format: p.file_format || '',
        file_name: p.file_name || p.title
      }
    },
    featuredFileData() {
      const p = this.featuredItem
      if (!p) return {}
      return {
        title: p.title,
        file_path: p.file_path,
        file_url: p.file_url,
        file_type: p.file_type || 'document',
        file_format: p.file_format || '',
        file_name: p.file_name || p.title
      }
    }
  },
  methods: {
    checkAuthStatus() {
      this.isAuthenticated = isAuthenticated()
      const userInfo = getUserInfo()
      this.isAdmin = userInfo && userInfo.role === 'admin'
    },
    async loadPasswordWarning() {
      if (!isAuthenticated()) {
        this.passwordWarning = null
        return
      }
      try {
        const { data } = await api.get('/auth/me')
        const u = data.user
        this.passwordWarning = u?.password_warning || null
        const old = getUserInfo()
        if (old && u) {
          setUserInfo({ ...old, ...u })
        }
      } catch (e) {
        this.passwordWarning = null
      }
    },
    loadConfig() {
      // 从localStorage加载配置
      const savedConfig = localStorage.getItem('homepageConfig')
      if (savedConfig) {
        try {
          this.homepageConfig = { ...DEFAULT_CONFIG, ...JSON.parse(savedConfig) }
        } catch (e) {
          console.error('加载首页配置失败:', e)
          this.homepageConfig = { ...DEFAULT_CONFIG }
        }
      }
    },
    saveConfig() {
      // 保存配置到localStorage
      localStorage.setItem('homepageConfig', JSON.stringify(this.homepageConfig))
      this.showConfigDialog = false
      this.$message.success('配置已保存')
      // 根据配置重新加载数据
      this.loadData()
    },
    loadData() {
      if (this.homepageConfig.showRecommendedCourseware) {
        this.loadRecommendedCourseware()
      }
      if (this.homepageConfig.showRecommendedVideos) {
        this.loadRecommendedVideos()
      }
      if (this.homepageConfig.showPolicies) {
        this.loadPolicies()
      }
      if (this.homepageConfig.showRanking) {
        this.loadRanking()
      }
      if (this.isAuthenticated) {
        this.loadCertificates()
      } else {
        this.certificates = []
      }
    },
    async loadRecommendedCourseware() {
      this.loadingCourseware = true
      try {
        const response = await getRecommendedCourseware({ limit: 6 })
        this.recommendedCourseware = response.data?.items || []
      } catch (error) {
        console.error('加载推荐课件失败:', error)
        this.recommendedCourseware = []
      } finally {
        this.loadingCourseware = false
      }
    },
    async loadRecommendedVideos() {
      this.loadingVideos = true
      try {
        const response = await getRecommendedVideos({ limit: 6 })
        this.recommendedVideos = response.data?.items || []
      } catch (error) {
        console.error('加载推荐视频失败:', error)
        this.recommendedVideos = []
      } finally {
        this.loadingVideos = false
      }
    },
    viewFeaturedItem(item) {
      this.featuredItem = item
      this.featuredDialogVisible = true
    },
    async loadPolicies() {
      this.loadingPolicies = true
      try {
        const response = await getPolicies({ limit: 50 })
        this.policies = response.data.policies || []
      } catch (error) {
        console.error('加载政策法规失败:', error)
        // 如果分类不存在，不显示错误
        if (error.response?.status !== 404) {
          this.$message.error('加载政策法规失败')
        }
      } finally {
        this.loadingPolicies = false
      }
    },
    async loadCertificates() {
      if (!this.isAuthenticated) return
      this.loadingCertificates = true
      try {
        const response = await getMyCertificates()
        this.certificates = response.data?.list || response.data?.certificates || []
      } catch (error) {
        console.error('加载证书失败:', error)
        this.certificates = []
      } finally {
        this.loadingCertificates = false
      }
    },
    goToCertificate(id) {
      this.$router.push(`/certificates/${id}`)
    },
    async loadRanking() {
      this.loadingRanking = true
      try {
        const response = await getStudentRanking({ 
          limit: 10, 
          type: this.rankingType 
        })
        console.log('排行榜响应:', response)
        if (response && response.data) {
          this.ranking = response.data.ranking || []
        } else {
          console.warn('排行榜响应格式异常:', response)
          this.ranking = []
        }
      } catch (error) {
        console.error('加载排行榜失败:', error)
        console.error('错误详情:', error.response?.data || error.message)
        this.$message.error(`加载排行榜失败: ${error.response?.data?.message || error.message || '未知错误'}`)
        this.ranking = []
      } finally {
        this.loadingRanking = false
      }
    },
    goToCourse(courseId) {
      this.$router.push(`/courses/${courseId}`)
    },
    goToStudy(courseId) {
      this.$router.push(`/courses/${courseId}/study`)
    },
    async handleEnrollCourse(course) {
      if (course.is_enrolled) return
      try {
        this.enrollingCourseId = course.id
        await enrollCourse(course.id)
        course.is_enrolled = true
        course.enrolled_count = (course.enrolled_count || 0) + 1
        this.$message.success('报名成功，可以开始学习了')
      } catch (e) {
        const msg = e.response?.data?.message || e.message || '报名失败'
        this.$message.error(msg)
      } finally {
        this.enrollingCourseId = null
      }
    },
    async viewPolicy(policy) {
      this.selectedPolicy = { ...policy }
      this.policyDialogVisible = true
      this.policyDetailLoading = true
      try {
        const response = await getDocumentById(policy.id)
        const doc = response.data?.document
        if (doc) {
          this.selectedPolicy = {
            ...policy,
            ...doc,
            content: doc.content ?? policy.content,
            created_at: doc.created_at ?? policy.created_at,
            updated_at: doc.updated_at ?? policy.updated_at
          }
        }
      } catch (error) {
        console.error('获取政策详情失败:', error)
        this.$message?.error('获取详情失败，仅显示摘要')
      } finally {
        this.policyDetailLoading = false
      }
    },
    normalizeFileUrl(path) {
      if (!path) return ''
      if (path.startsWith('http://') || path.startsWith('https://')) return path
      if (path.startsWith('/api/')) return path
      if (path.startsWith('/uploads/')) return `/api${path}`
      if (path.startsWith('uploads/')) return `/api/${path}`
      return path
    },
    getPolicyFileUrl(policy) {
      if (!policy) return ''
      const candidate = policy.file_url || policy.file_path
      return this.normalizeFileUrl(candidate)
    },
    downloadPolicyFile(policy) {
      const fileUrl = this.getPolicyFileUrl(policy)
      if (!fileUrl) {
        this.$message?.error('无法获取文件地址')
        return
      }
      const downloadUrl = fileUrl.includes('?') ? `${fileUrl}&download=1` : `${fileUrl}?download=1`
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = policy.title || policy.file_name || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$message?.success('开始下载')
    },
    downloadPolicyAsText(policy) {
      if (!policy?.content) {
        this.$message?.error('暂无文本内容')
        return
      }
      const blob = new Blob([policy.content], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = (policy.title || '政策法规') + '.txt'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      this.$message?.success('已下载为文本')
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    getRankClass(rank) {
      if (rank === 1) return 'rank-gold'
      if (rank === 2) return 'rank-silver'
      if (rank === 3) return 'rank-bronze'
      return ''
    },
    getCoverUrl(cover) {
      if (!cover) return ''
      // 如果已经是完整URL，直接返回
      if (cover.startsWith('http://') || cover.startsWith('https://')) {
        return cover
      }
      // 如果已经是 /api/uploads/ 开头，直接返回
      if (cover.startsWith('/api/uploads/')) {
        return cover
      }
      // 如果是 /uploads/ 开头，添加 /api 前缀
      if (cover.startsWith('/uploads/')) {
        return `/api${cover}`
      }
      // 如果是 / 开头但不是 /uploads/，直接添加 /api 前缀
      if (cover.startsWith('/')) {
        return `/api${cover}`
      }
      // 否则添加 /api/uploads/ 前缀
      return `/api/uploads/${cover}`
    },
    onWorkGuidanceLoaded(config) {
      this.workGuidance = config || { enabled: false }
    },
    openWorkGuidance() {
      this.$refs.workGuidanceEntry?.openDialog()
    }
  },
  mounted() {
    this.checkAuthStatus()
    this.loadConfig()
    this.loadData()
    this.loadPasswordWarning()
    // 监听存储事件以更新认证状态
    window.addEventListener('storage', this.checkAuthStatus)
  },
  beforeUnmount() {
    // 移除事件监听器
    window.removeEventListener('storage', this.checkAuthStatus)
  }
}
</script>

<style scoped>
.home {
  min-height: 100vh;
  position: relative;
  background-color: #f5f7fa;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  background-attachment: fixed;
}

.home::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(245, 247, 250, 0.6);
  z-index: 0;
  pointer-events: none;
}

.home > * {
  position: relative;
  z-index: 1;
}

.password-warning-bar {
  max-width: 1200px;
  margin: 0 auto 16px;
  padding: 0 16px;
}

/* 顶部横幅 */
.home-banner {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
  padding: 40px 0;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.banner-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.banner-title {
  font-size: 36px;
  font-weight: bold;
  margin: 0 0 10px 0;
  letter-spacing: 2px;
}

.banner-subtitle {
  font-size: 18px;
  margin: 0;
  opacity: 0.9;
  letter-spacing: 1px;
}

.banner-actions {
  margin-top: 18px;
  display: flex;
  justify-content: center;
}

.banner-guidance-btn {
  background: #fff !important;
  color: #1e3c72 !important;
  border: none !important;
  font-weight: 600;
  padding: 10px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.banner-guidance-btn:hover {
  opacity: 0.95;
}

/* 主内容：左侧 2x2 板块 + 右侧排行榜，中间分割线 */
.main-content.home-layout {
  max-width: 1280px;
  margin: 24px auto;
  padding: 0 20px;
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.home-layout .left-column {
  flex: 1;
  min-width: 0;
}

.home-layout .blocks-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

/* 分割线：低调竖线，上下略淡 */
.layout-divider {
  width: 1px;
  align-self: stretch;
  min-height: 280px;
  margin: 0 20px;
  background: linear-gradient(
    to bottom,
    rgba(228, 231, 237, 0.3) 0%,
    rgba(228, 231, 237, 0.8) 15%,
    rgba(228, 231, 237, 0.8) 85%,
    rgba(228, 231, 237, 0.3) 100%
  );
  flex-shrink: 0;
}

.ranking-column {
  width: 300px;
  flex-shrink: 0;
}

/* 固定高度板块（最多 3 条可见 + 滚动） */
.content-block.fixed-block {
  height: 280px;
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.block-search {
  padding: 6px 12px;
  flex-shrink: 0;
}

.block-search-input {
  width: 100%;
}

.block-list-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 12px 12px;
}

.scroll-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 156px;
  overflow-y: auto;
}

.scroll-list::-webkit-scrollbar {
  width: 6px;
}

.scroll-list::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

/* 内容块通用样式 */
.content-block {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  overflow: hidden;
}

.block-header {
  background: linear-gradient(90deg, #1e3c72 0%, #2a5298 100%);
  padding: 8px 14px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-height: 0;
}

.block-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.block-icon {
  font-size: 18px;
  color: white;
  flex-shrink: 0;
}

.block-title {
  font-size: 15px;
  font-weight: bold;
  color: white;
  margin: 0;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.block-line {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #ff6b6b;
}

.block-body {
  padding: 20px;
  min-height: 200px;
}

/* 推荐课程样式 */
.courses-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.course-item {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
}

.course-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #409eff;
}

.course-cover {
  width: 100%;
  height: 140px;
  overflow: hidden;
  background: #f0f2f5;
}

.course-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.course-content {
  padding: 12px;
}

.course-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 8px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.course-description {
  font-size: 13px;
  color: #606266;
  margin: 0 0 10px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-footer {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
}

.course-actions {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
}
.course-actions .el-button {
  width: 100%;
}

.course-teacher,
.course-stats {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 政策法规样式 */
.policies-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.policy-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.policy-item:last-child {
  border-bottom: none;
}

.policy-item:hover {
  background-color: #f5f7fa;
}

.policy-bullet {
  width: 8px;
  height: 8px;
  background: #409eff;
  border-radius: 50%;
  margin-top: 6px;
  margin-right: 12px;
  flex-shrink: 0;
}

.policy-content {
  flex: 1;
}

.policy-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin: 0;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.policy-excerpt {
  font-size: 14px;
  color: #606266;
  margin: 0 0 8px 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.policy-date {
  font-size: 12px;
  color: #909399;
}

.policy-arrow {
  font-size: 18px;
  color: #c0c4cc;
  margin-left: 10px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* 板块内课程行、链接行、证书行 */
.courses-list .course-row,
.links-list .link-row,
.certs-list .cert-row {
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
  transition: background-color 0.2s;
}

.courses-list .course-row:last-child,
.links-list .link-row:last-child,
.certs-list .cert-row:last-child {
  border-bottom: none;
}

.course-row:hover,
.cert-row:hover {
  background-color: #f5f7fa;
}

.course-row-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.course-row-title {
  flex: 1;
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.course-row-actions .el-button {
  flex-shrink: 0;
}

.link-row .law-link {
  display: flex;
  align-items: center;
  padding: 6px 0;
  color: #409eff;
  text-decoration: none;
  font-size: 14px;
}

.link-row .law-link:hover {
  color: #66b1ff;
}

.cert-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cert-row-title {
  flex: 1;
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.policy-dialog-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.policy-dialog-tip {
  font-size: 12px;
  color: #909399;
  margin-right: auto;
}

/* 法律法规链接样式 */
.law-links-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.law-links-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #409eff;
}

.law-links-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.law-link-item {
  margin-bottom: 10px;
}

.law-link {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  color: #409eff;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s;
  border: 1px solid #e4e7ed;
  background: #f5f7fa;
}

.law-link:hover {
  background: #ecf5ff;
  border-color: #409eff;
  color: #66b1ff;
  transform: translateX(3px);
}

.law-link-icon {
  font-size: 16px;
  margin-right: 8px;
  flex-shrink: 0;
}

.law-link span {
  font-size: 14px;
  line-height: 1.5;
}

/* 排行榜样式 */
.ranking-block {
  position: sticky;
  top: 20px;
}

.ranking-tabs-wrapper {
  padding: 15px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  text-align: center;
}

.ranking-list {
  padding: 10px 0;
}

.ranking-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f2f5;
  transition: background-color 0.3s;
}

.ranking-item:last-child {
  border-bottom: none;
}

.ranking-item:hover {
  background-color: #f5f7fa;
}

.ranking-item.top-three {
  background: linear-gradient(90deg, rgba(30, 60, 114, 0.05) 0%, transparent 100%);
}

.rank-number {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  border-radius: 50%;
  margin-right: 15px;
  background-color: #f5f7fa;
  color: #606266;
  flex-shrink: 0;
}

.rank-number.rank-gold {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #fff;
}

.rank-number.rank-silver {
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
  color: #fff;
}

.rank-number.rank-bronze {
  background: linear-gradient(135deg, #cd7f32 0%, #e6a85c 100%);
  color: #fff;
}

.rank-number .medal {
  font-size: 20px;
}

.rank-info {
  flex: 1;
}

.rank-name {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.rank-stats {
  font-size: 13px;
  color: #606266;
}

.empty-state {
  padding: 24px 0;
  text-align: center;
}

.fixed-block .empty-state {
  padding: 16px 0;
}

/* 配置按钮 */
.config-button {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 政策法规详情 */
.policy-detail {
  padding: 20px 0;
}

.policy-meta {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #666;
}

.policy-body {
  font-size: 14px;
  line-height: 1.8;
  color: #333;
}

.policy-file-preview {
  min-height: 60vh;
  margin-top: 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
}

.policy-no-content {
  padding: 20px 0;
  min-height: 80px;
}

.policy-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content.home-layout {
    flex-direction: column;
  }

  .layout-divider {
    width: 100%;
    height: 1px;
    min-height: 0;
    margin: 16px 0;
    background: linear-gradient(
      to right,
      rgba(228, 231, 237, 0.3) 0%,
      rgba(228, 231, 237, 0.8) 20%,
      rgba(228, 231, 237, 0.8) 80%,
      rgba(228, 231, 237, 0.3) 100%
    );
  }

  .ranking-column {
    width: 100%;
  }

  .ranking-block {
    position: static;
  }
}

@media (max-width: 768px) {
  .home-banner {
    padding: 30px 0;
  }
  
  .banner-title {
    font-size: 28px;
  }
  
  .banner-subtitle {
    font-size: 16px;
  }
  
  .main-content {
    margin: 20px auto;
    padding: 0 15px;
    gap: 20px;
  }
  
  .courses-grid {
    grid-template-columns: 1fr;
  }
  
  .block-body {
    padding: 15px;
  }

  .config-button {
    bottom: 20px;
    right: 20px;
  }
}
.home-beian {
  max-width: 1280px;
  margin: 8px auto 24px;
  padding: 0 20px 8px;
  text-align: center;
  font-size: 13px;
  line-height: 1.6;
}

.home-beian a {
  color: rgba(44, 62, 80, 0.72);
  text-decoration: none;
}

.home-beian a:hover {
  color: #2a5298;
  text-decoration: underline;
}
</style>
